import { useCallback, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../utils/configurationProvider";
import "./index.css";
import { Popover } from "@mui/material";
import SVGComponent from "../../_utils/SVGComponent";

export default function CustomizedBreadcrumbs({
  navigationLockRef,
  setShrinkStart,
  getSession,
  breadcrumbStack,
  animatingIndex,
  shrinkStart,
  hiddenBreadcrumbs,
  collapsedBreadcrumbs,
}) {
  const navigate = useNavigate();

  /* ---------------- POPOVER STATE ---------------- */

  const [anchorEl, setAnchorEl] = useState(null);
  const closeTimer = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const handleOpen = (e) => {
    if (anchorEl) return;
    clearTimeout(closeTimer.current);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    closeTimer.current = setTimeout(() => {
      setAnchorEl(null);
    }, 200);
  };

  const open = Boolean(anchorEl);

  /* ---------------- LABEL FORMAT ---------------- */

  const formatBreadcrumb = useCallback((crumb) => {
    return crumb
      .split("_")
      .map((p, i) =>
        i === 0 ? p.charAt(0).toUpperCase() + p.slice(1) : p.toLowerCase(),
      )
      .join(" ");
  }, []);

  /* ---------------- CLICK HANDLER ---------------- */

  const handleBreadcrumbClick = (index) => {
    if (navigationLockRef.current) return;

    navigationLockRef.current = true;
    setShrinkStart(index);

    const currentIdx = window.history.state?.idx ?? 0;
    const parsed = getSession();
    const targetPath = breadcrumbStack[index]?.fullPath;

    const historyKeys = Object.keys(parsed)
      .map(Number)
      .filter((k) => k < currentIdx)
      .sort((a, b) => b - a);

    const targetHistoryIdx = historyKeys.find((key) => {
      const stack = parsed[key];
      return (
        stack &&
        stack[index] &&
        stack[index].fullPath === targetPath &&
        stack.length === index + 1
      );
    });

    if (targetHistoryIdx !== undefined) {
      const delta = targetHistoryIdx - currentIdx;
      delta !== 0 ? navigate(delta) : navigate(targetPath);
    }

    setTimeout(() => {
      setShrinkStart(null);
      navigationLockRef.current = false;
    }, IntervalConfiguration?.intervalSpeedTicker || 300);
  };

  if (!breadcrumbStack.length) return null;

  /* ---------------- RENDER ---------------- */

  return (
    <div className="breadcrumb-wrapper overflow-x-auto whitespace-nowrap max-w-tab-width-normal mx-auto">
      <div className="flex flex-row items-center w-full">
        {collapsedBreadcrumbs.map((crumb, index) => {
          const isCollapsed = crumb.fullPath === "__collapsed__";

          /* ---------- COLLAPSED (...) ---------- */
          if (isCollapsed) {
            return (
              <span
                key={`collapsed-${index}`}
                className="flex items-center"
              >
                <span
                  className="crumb cursor-pointer text-text-disabled"
                  onMouseEnter={handleOpen}
                  onMouseLeave={handleClose}
                >
                  .....
                </span>

                <Popover
                  open={open}
                  anchorEl={anchorEl}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  disableRestoreFocus
                  sx={{
                    pointerEvents: "none",
                  }}
                  PaperProps={{
                    className: "bg-text-primary p-padding-md mt-margin-md",
                    onMouseEnter: () => clearTimeout(closeTimer.current),
                    onMouseLeave: handleClose,
                    sx: {
                      pointerEvents: "auto",
                      borderRadius: "var(--border-radius-md)",
                    },
                  }}
                >
                  <div className="flex flex-col relative gap-gap-sm">
                    {hiddenBreadcrumbs.map((hidden) => {
                      const realIndex = breadcrumbStack.findIndex(
                        (c) => c.fullPath === hidden.fullPath,
                      );

                      return (
                        <div
                          key={realIndex}
                          className="flex gap-gap-sm flex-grow items-center justify-between p-padding-sm rounded-border-radius-l cursor-pointer global-hover-blue relative z-z-index-base"
                          onMouseEnter={() => setHoveredIndex(realIndex)}
                          onMouseLeave={() => setHoveredIndex(null)}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setAnchorEl(null);
                            handleBreadcrumbClick(realIndex);
                          }}
                        >
                          <div className="flex flex-row items-center gap-x-gap-md">
                            <p className="text-font-md text-primary-bg font-normal">
                              {formatBreadcrumb(hidden.label)}
                            </p>
                          </div>

                          <div
                            style={{
                              flexShrink: "0",
                              visibility:
                                hoveredIndex === realIndex
                                  ? "visible"
                                  : "hidden",
                            }}
                          >
                            <SVGComponent
                              url="https://q2pay.in/new_assets/common/arrows/arrow_right_white.svg"
                              width={"var(--icon-sm)"}
                              height={"var(--icon-sm)"}
                              color={"var(--primary-bg)"}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Popover>
              </span>
            );
          }

          /* ---------- NORMAL BREADCRUMB ---------- */

          const realIndex = breadcrumbStack.findIndex(
            (c) => c.fullPath === crumb.fullPath,
          );

          const isLast =
            index ===
            collapsedBreadcrumbs.length - GlobalConfiguration.lengthCheckOne;
          const isFirst = index === GlobalConfiguration.lengthCheckZero;
          const isEntering = animatingIndex === realIndex;

          return (
            <span
              key={crumb.fullPath}
              className="flex items-center"
            >
              {!isFirst && (
                <span
                  className={`mx-margin-sm ${
                    isLast ? "text-primary-bg" : "text-text-disabled"
                  }`}
                >
                  &gt;
                </span>
              )}

              <span
                className={`breadcrumb-text text-font-sm block text-ellipsis
                  ${
                    isLast
                      ? "text-primary-bg text-bg font-semibold cursor-default"
                      : "text-text-disabled cursor-pointer hover:underline"
                  }
                  ${
                    shrinkStart !== null && realIndex > shrinkStart
                      ? "shrink-active"
                      : ""
                  }
                  ${isEntering ? "fade-in-left-to-right" : ""}`}
                onClick={() => !isLast && handleBreadcrumbClick(realIndex)}
              >
                {formatBreadcrumb(crumb.label)}
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}
